import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { PROJECT_TYPES } from "../../../../../constants/projectTypes";
import { wbsLabel } from "../utils/treeLabels";
import { useTreeViewContext } from "../TreeViewContext";

// Right-click menu on a WBS row. anchorPosition so it opens at the pointer,
// the way a desktop context menu does.
const WbsContextMenu = () => {
  const { wbsOperations } = useTreeViewContext();
  const { wbsMenu, closeWbsMenu, openEditWbs, openDeleteWbs } = wbsOperations;

  return (
    <Menu
      open={Boolean(wbsMenu)}
      onClose={closeWbsMenu}
      anchorReference="anchorPosition"
      anchorPosition={
        wbsMenu ? { top: wbsMenu.mouseY, left: wbsMenu.mouseX } : undefined
      }
    >
      <MenuItem onClick={() => openEditWbs(wbsMenu.plant, wbsMenu.wbs)}>
        <ListItemIcon>
          <EditIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Modify</ListItemText>
      </MenuItem>
      <MenuItem
        onClick={() => openDeleteWbs(wbsMenu.plant, wbsMenu.wbs)}
        sx={{ color: "error.main" }}
      >
        <ListItemIcon>
          <DeleteOutlineIcon fontSize="small" color="error" />
        </ListItemIcon>
        <ListItemText>Delete</ListItemText>
      </MenuItem>
    </Menu>
  );
};

// Modify: the same WBS code and description the Add WBS dialog asks for,
// prefilled. Type and SharePoint link are deliberately left out - a WBS's
// type decides which module opens it and what its saved data means, so it
// is not something to change from a right-click.
const EditWbsDialog = () => {
  const { wbsOperations } = useTreeViewContext();
  const {
    editWbsTarget,
    setEditWbsTarget,
    editWbsForm,
    setEditWbsForm,
    savingWbsEdit,
    editWbsError,
    handleUpdateWbs,
  } = wbsOperations;

  return (
    <Dialog
      open={Boolean(editWbsTarget)}
      onClose={() => !savingWbsEdit && setEditWbsTarget(null)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Modify WBS — {wbsLabel(editWbsTarget?.wbs)}</DialogTitle>
      <DialogContent>
        <Box
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
        >
          <TextField
            label="WBS Code"
            value={editWbsForm.projectName}
            onChange={(event) =>
              setEditWbsForm((prev) => ({
                ...prev,
                projectName: event.target.value,
              }))
            }
            fullWidth
            required
            autoFocus
            helperText="Half of every revision's Rev UID - renaming it renames the revisions saved from here on"
          />
          <TextField
            label="Description (optional)"
            value={editWbsForm.description}
            onChange={(event) =>
              setEditWbsForm((prev) => ({
                ...prev,
                description: event.target.value,
              }))
            }
            helperText="Shown under the WBS UID in the module headers"
            fullWidth
            multiline
            minRows={2}
          />
          {editWbsError && (
            <Typography variant="caption" color="error">
              {editWbsError}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => setEditWbsTarget(null)}
          disabled={savingWbsEdit}
        >
          Cancel
        </Button>
        <Button
          onClick={handleUpdateWbs}
          variant="contained"
          disabled={savingWbsEdit}
        >
          {savingWbsEdit ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Delete. Names what goes with it: a WBS's revisions live inside it, so
// they go too.
const DeleteWbsDialog = () => {
  const { wbsOperations } = useTreeViewContext();
  const {
    deleteWbsTarget,
    setDeleteWbsTarget,
    deletingWbs,
    deleteWbsError,
    handleDeleteWbs,
  } = wbsOperations;

  return (
    <Dialog
      open={Boolean(deleteWbsTarget)}
      onClose={() => !deletingWbs && setDeleteWbsTarget(null)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Delete WBS?</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ wordBreak: "break-word" }}>
          {`Delete ${wbsLabel(deleteWbsTarget?.wbs)} and every revision saved under it? This cannot be undone.`}
        </DialogContentText>
        {deleteWbsError && (
          <Typography
            variant="caption"
            color="error"
            sx={{ display: "block", mt: 2 }}
          >
            {deleteWbsError}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => setDeleteWbsTarget(null)}
          disabled={deletingWbs}
        >
          Cancel
        </Button>
        <Button
          onClick={handleDeleteWbs}
          variant="contained"
          color="error"
          disabled={deletingWbs}
        >
          {deletingWbs ? "Deleting…" : "Delete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const AddWbsDialog = () => {
  const { wbsOperations } = useTreeViewContext();
  const {
    addWbsTarget,
    setAddWbsTarget,
    wbsForm,
    setWbsForm,
    lockedWbsType,
    savingWbs,
    wbsError,
    isSingletonTypeTaken,
    handleCreateWbs,
  } = wbsOperations;

  return (
    <Dialog
      open={Boolean(addWbsTarget)}
      onClose={() => !savingWbs && setAddWbsTarget(null)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>
        Add {lockedWbsType ? `${lockedWbsType.toUpperCase()} ` : ""}WBS —{" "}
        {addWbsTarget?.name}
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
        >
          <TextField
            label="WBS Code"
            value={wbsForm.projectName}
            onChange={(event) =>
              setWbsForm((prev) => ({
                ...prev,
                projectName: event.target.value,
              }))
            }
            fullWidth
            required
            autoFocus
          />
          <TextField
            label="Description (optional)"
            value={wbsForm.description}
            onChange={(event) =>
              setWbsForm((prev) => ({
                ...prev,
                description: event.target.value,
              }))
            }
            helperText="Shown under the WBS UID in the module headers"
            fullWidth
            multiline
            minRows={2}
          />
          <TextField
            label="Type"
            select
            value={wbsForm.type}
            onChange={(event) =>
              setWbsForm((prev) => ({ ...prev, type: event.target.value }))
            }
            fullWidth
            required
            disabled={Boolean(lockedWbsType)}
            helperText={
              lockedWbsType
                ? "Set by the folder this was added from"
                : undefined
            }
          >
            {(lockedWbsType
              ? PROJECT_TYPES.filter((type) => type.value === lockedWbsType)
              : PROJECT_TYPES
            ).map((type) => {
              // This picker JSX is evaluated on every render, including
              // ones where the dialog is closed and addWbsTarget is
              // null - skip the lookup instead of resolving against
              // an undefined plant id.
              const taken = addWbsTarget
                ? isSingletonTypeTaken(addWbsTarget, type.value)
                : false;
              return (
                <MenuItem
                  key={type.value}
                  value={type.value}
                  disabled={taken}
                >
                  {taken ? `${type.label} (only one per plant)` : type.label}
                </MenuItem>
              );
            })}
          </TextField>
          <TextField
            label="SharePoint Link (optional)"
            value={wbsForm.link}
            onChange={(event) =>
              setWbsForm((prev) => ({ ...prev, link: event.target.value }))
            }
            fullWidth
            type="url"
          />
          {wbsError && (
            <Typography variant="caption" color="error">
              {wbsError}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setAddWbsTarget(null)} disabled={savingWbs}>
          Cancel
        </Button>
        <Button
          onClick={handleCreateWbs}
          variant="contained"
          disabled={savingWbs}
        >
          {savingWbs ? "Creating…" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const WbsDialogs = () => (
  <>
    <WbsContextMenu />
    <EditWbsDialog />
    <DeleteWbsDialog />
    <AddWbsDialog />
  </>
);
